// React Query bindings for the categories endpoints.
// Centralised so both this feature and any future caller share one cache key.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost } from "@/lib/api/client";
import type { Category, CategoryFormPayload } from "./types";

const QUERY_KEY = ["categories"] as const;

export function useCategoriesQuery() {
  return useQuery({
    queryKey: QUERY_KEY,
    // BE marshals empty children slices as null; coalesce here so .map/.length stay safe.
    queryFn: async () => {
      const tree = await apiGet<Category[] | null>("/v1/categories");
      return tree ?? [];
    },
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CategoryFormPayload) => apiPost("/v1/categories", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useUpdateCategory(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CategoryFormPayload) => apiPatch(`/v1/categories/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/v1/categories/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}
